
import { useState } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

interface Property {
  id: string;
  title: string;
  price_egp: number;
  deposit_egp: number;
  distance_km: number;
  unit_type: string;
  furnished: boolean;
  verified: boolean;
  photos: string[];
  amenities: string[];
  available_from: string;
  lease_min_months: number;
  gender_rules: string;
  universities?: {
    name_en: string;
    name_ar: string;
  };
}

export const usePropertyComparison = () => {
  const { t } = useLanguage();
  const [comparisonList, setComparisonList] = useState<Property[]>([]);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);

  const addToComparison = (property: Property) => {
    if (comparisonList.length >= 3) {
      toast.error(t('max_comparison_reached', 'يمكنك مقارنة 3 عقارات كحد أقصى', 'You can compare up to 3 properties'));
      return;
    }

    if (comparisonList.find(p => p.id === property.id)) {
      toast.info(t('already_in_comparison', 'هذا العقار موجود بالفعل في المقارنة', 'Property already in comparison'));
      return;
    }

    setComparisonList(prev => [...prev, property]);
    toast.success(t('added_to_comparison', 'تم إضافة العقار للمقارنة', 'Property added to comparison'));
  };

  const removeFromComparison = (propertyId: string) => {
    setComparisonList(prev => prev.filter(p => p.id !== propertyId));
    toast.success(t('removed_from_comparison', 'تم حذف العقار من المقارنة', 'Property removed from comparison'));
  };

  const clearComparison = () => {
    setComparisonList([]);
    setIsComparisonOpen(false);
  };

  const openComparison = () => {
    if (comparisonList.length < 2) {
      toast.error(t('need_two_properties', 'تحتاج على الأقل عقارين للمقارنة', 'You need at least 2 properties to compare'));
      return;
    }
    setIsComparisonOpen(true);
  };

  const closeComparison = () => {
    setIsComparisonOpen(false);
  };

  return {
    comparisonList,
    isComparisonOpen,
    addToComparison,
    removeFromComparison,
    clearComparison,
    openComparison,
    closeComparison
  };
};
